import { TypedEventEmitter } from './event-emitter.js';
import { DEFAULT_THROW_CONFIG } from './constants.js';
import type {
  ThrowDetectorConfig,
  ThrowDetectorEventMap,
  ThrowStartEvent,
  AirborneEvent,
  CaughtEvent,
  LandedEvent,
} from './types.js';

export class ThrowDetector extends TypedEventEmitter<ThrowDetectorEventMap> {
  private config: ThrowDetectorConfig;

  // CC state
  private freeFall = 0;
  private spinSmoothed = 0;

  // Throw state
  private _airborne = false;
  private throwStartTime = 0;
  private throwLastUpdateTime = 0;
  private throwPeakFreeFall = 0;
  private throwEverSpun = false;

  get airborne(): boolean {
    return this._airborne;
  }

  constructor(config?: Partial<ThrowDetectorConfig>) {
    super();
    this.config = { ...DEFAULT_THROW_CONFIG, ...config };
  }

  /** Process a CC update relevant to throw detection */
  handleCC(cc: number, value: number, timestamp: number): void {
    switch (cc) {
      case 1: { // SpinSmoothed
        this.spinSmoothed = value;
        if (this._airborne && value >= this.config.spinningThreshold) {
          this.throwEverSpun = true;
        }
        if (this._airborne) {
          this.maybeEmitAirborneUpdate(timestamp);
        }
        break;
      }

      case 2: { // FreeFall
        const prev = this.freeFall;
        this.freeFall = value;

        // Throw start — no rolling check; coordination happens externally
        if (value >= this.config.freeFallThreshold && !this._airborne) {
          this.startThrow(timestamp);
        }

        if (this._airborne && value > this.throwPeakFreeFall) {
          this.throwPeakFreeFall = value;
        }

        if (this._airborne) {
          this.maybeEmitAirborneUpdate(timestamp);
        }

        // Throw end (landed): freefall drops below threshold
        if (
          this._airborne &&
          value < this.config.freeFallThreshold &&
          prev >= this.config.freeFallThreshold
        ) {
          this.endThrowLanded(timestamp);
        }
        break;
      }
    }
  }

  /** Process a tap — catch detection */
  handleTap(velocity: number, timestamp: number): void {
    if (this._airborne) {
      this.endThrowCaught(timestamp, velocity);
    }
  }

  // ── Throw lifecycle ────────────────────────────────────────────────

  private startThrow(timestamp: number): void {
    this._airborne = true;
    this.throwStartTime = timestamp;
    this.throwLastUpdateTime = timestamp;
    this.throwPeakFreeFall = this.freeFall;
    this.throwEverSpun = this.spinSmoothed >= this.config.spinningThreshold;

    const event: ThrowStartEvent = { type: 'throwStart', timestamp };
    this.emit('throwStart', event);
  }

  private maybeEmitAirborneUpdate(timestamp: number): void {
    if (!this._airborne) return;
    if (timestamp - this.throwLastUpdateTime < this.config.airborneUpdateIntervalMs) return;

    this.throwLastUpdateTime = timestamp;

    const event: AirborneEvent = {
      type: 'airborne',
      startTime: this.throwStartTime,
      elapsed: timestamp - this.throwStartTime,
      freeFallIntensity: this.freeFall,
      spinSpeed: this.spinSmoothed,
      spinning: this.spinSmoothed >= this.config.spinningThreshold,
    };
    this.emit('airborne', event);
  }

  private endThrowCaught(timestamp: number, catchVelocity: number): void {
    const event: CaughtEvent = {
      type: 'caught',
      throwTime: this.throwStartTime,
      catchTime: timestamp,
      airborneMs: timestamp - this.throwStartTime,
      catchVelocity,
      peakFreeFall: this.throwPeakFreeFall,
      wasSpinning: this.throwEverSpun,
    };
    this._airborne = false;
    this.emit('caught', event);
  }

  private endThrowLanded(timestamp: number): void {
    const event: LandedEvent = {
      type: 'landed',
      throwTime: this.throwStartTime,
      landTime: timestamp,
      airborneMs: timestamp - this.throwStartTime,
      peakFreeFall: this.throwPeakFreeFall,
      wasSpinning: this.throwEverSpun,
    };
    this._airborne = false;
    this.emit('landed', event);
  }
}
